import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';

const FundamentalsContext = createContext();

export const useFundamentalsData = () => useContext(FundamentalsContext);

export const FundamentalsProvider = ({ children }) => {
    const [rows, setRows] = useState([]);

    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const response = await axios.get(`${API_URL}/fundamentals?limit=1000`);
                if (response.data) {
                    setRows(response.data);
                }
            } catch (error) {
                console.error("Error fetching fundamentals data:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    const updateRow = async (index, updatedRow) => {
        const rowToUpdate = rows[index];
        if (!rowToUpdate.id) {
            console.error("Cannot update row without ID");
            return;
        }

        try {
            // Optimistic update
            const newRows = [...rows];
            newRows[index] = updatedRow;
            setRows(newRows);

            const payload = {
                category: updatedRow.Category,
                topic: updatedRow.Topic,
                catalyst: updatedRow.Catalyst,
                headline: updatedRow.Headline,
                date: updatedRow.Date,
                interpretation: updatedRow.Interpretation,
                impact: updatedRow.Impact,
                assets: updatedRow.Assets,
                sources: updatedRow.Sources
            };
            await axios.put(`${API_URL}/fundamentals/${rowToUpdate.id}`, payload);
        } catch (error) {
            console.error("Error updating fundamental event:", error);
            // Revert on error (could be improved)
            window.location.reload();
        }
    };

    const deleteRow = async (index) => {
        const rowToDelete = rows[index];
        if (!rowToDelete.id) {
            console.error("Cannot delete row without ID");
            return;
        }

        try {
            // Optimistic update
            setRows(prev => prev.filter((_, i) => i !== index));
            await axios.delete(`${API_URL}/fundamentals/${rowToDelete.id}`);
        } catch (error) {
            console.error("Error deleting fundamental event:", error);
            window.location.reload();
        }
    };

    const addRow = async (initialData = {}) => {
        const newRowData = {
            category: initialData.Category || "New Category",
            topic: initialData.Topic || null,
            catalyst: initialData.Catalyst || "New Catalyst",
            headline: initialData.Headline || "New Headline",
            date: initialData.Date || new Date().toISOString().split('T')[0],
            interpretation: initialData.Interpretation || "New Interpretation",
            impact: initialData.Impact || "Low",
            assets: initialData.Assets || "EUR/USD",
            sources: initialData.Sources || "Source"
        };

        try {
            const response = await axios.post(`${API_URL}/fundamentals`, newRowData);
            if (response.data && response.data.id) {
                const newRow = {
                    id: response.data.id,
                    Category: newRowData.category,
                    Topic: newRowData.topic,
                    Catalyst: newRowData.catalyst,
                    Headline: newRowData.headline,
                    Date: newRowData.date,
                    Interpretation: newRowData.interpretation,
                    Impact: newRowData.impact,
                    Assets: newRowData.assets,
                    Sources: newRowData.sources
                };
                setRows(prev => [...prev, newRow]);
            }
        } catch (error) {
            console.error("Error creating fundamental event:", error);
        }
    };

    const resetData = async () => {
        setIsLoading(true);
        try {
            // Artificial delay to show animation (UX feedback)
            await new Promise(resolve => setTimeout(resolve, 400));
            const response = await axios.get(`${API_URL}/fundamentals?limit=1000`);
            if (response.data) {
                setRows(response.data);
            }
        } catch (error) {
            console.error("Error resetting data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Helper to find the original index since tabs filter the data
    const getOriginalIndex = (row) => {
        return rows.findIndex(r => r.id === row.id);
    };

    const setAllData = (newRows) => {
        setRows(newRows);
    };

    const reorderRows = (startIndex, endIndex) => {
        setRows(prev => {
            const result = Array.from(prev);
            const [removed] = result.splice(startIndex, 1);
            result.splice(endIndex, 0, removed);
            return result;
        });
    };

    const [impactFilter, setImpactFilter] = useState('All');

    const filteredRows = React.useMemo(() => {
        if (impactFilter === 'All') return rows;

        return rows.filter(row => {
            if (!row.Impact) return false;
            const impact = row.Impact;

            switch (impactFilter) {
                case 'Very High':
                    return impact.includes('Very High');
                case 'High':
                    return impact.includes('High');
                case 'Medium':
                    return impact.includes('Medium');
                case 'Low':
                    return impact.includes('Low');
                default:
                    return true;
            }
        });
    }, [rows, impactFilter]);

    return (
        <FundamentalsContext.Provider value={{
            rows,
            filteredRows,
            impactFilter,
            setImpactFilter,
            updateRow,
            deleteRow,
            addRow,
            getOriginalIndex,
            setAllData,
            resetData,
            reorderRows,
            isLoading
        }}>
            {children}
        </FundamentalsContext.Provider>
    );
};
